// app/frontend/services/customerService.ts
import apiClient from "@/lib/apiClient";
import type { ImageVariants } from "@/components/ResponsiveImage";

type Customer = {
  id: number;
  name: string;
  email: string;
  phone: string;
};

export const fetchCustomers = async (siteId: number): Promise<Customer[]> => {
  const response = await apiClient.get<Customer[]>(`/dashboard/sites/${siteId}/customers`);
  return response.data;
};

export type SiteBanner = {
  id: number;
  image_url: string | null;
  image_variant_urls: Record<string, string> | null;
  link_url: string | null;
};

export type Site = {
  id: number;
  uid?: string;
  name: string;
  subdomain?: string;
  slug: string;
  description?: string;
  thumbnail_url?: string;
  thumbnail_variant_urls?: ImageVariants;
  icon_url?: string;
  icon_variant_urls?: ImageVariants;
  privacy_url?: string;
  terms_url?: string;
  trade_law_url?: string;
  created_at?: string;
  // 拡張情報
  active_events_count?: number;
  total_events_count?: number;
  categories?: string[];
  is_new?: boolean;
  is_popular?: boolean;
  latest_event?: {
    uid: string;
    name: string;
    thumbnail_url?: string;
  };
  // 認証・実績情報
  identity_verified?: boolean;
  antique_dealer_verified?: boolean;
  delivery_count?: number;
  // 設定
  point_purchase_enabled?: boolean;
  // 決済プロバイダー
  payment_provider?: 'fincode' | 'gmo_pg';
  // Owner が顧客に出す決済手段（軸2）。顧客側の決済手段表示の単一ソース
  accepted_payment_methods?: string[];
  // 友達招待コンバージョン条件
  referral_conversion_point?: 'registration' | 'phone_verified' | 'first_purchase';
  // Xシェア設定
  x_account?: string;
  share_reward_enabled?: boolean;
  share_reward_points?: number;
  // 結果ページバナー
  result_banner_url?: string | null;
  result_banner_link?: string | null;
  // バナー
  banners?: SiteBanner[];
};

// 等級別の在庫情報
export type PrizeAllocation = {
  id: number;
  product_name: string | null;
  grade: string | null;
  point_value: number | null;
  image_url: string | null;
  image_variant_urls?: ImageVariants;
};

export type PrizeWithAllocations = {
  id: number | null;
  name: string;
  logic_type: string | null;
  display_thumbnail_mode?: "products" | "custom_thumbnails";
  display_thumbnail_urls?: string[];
  display_thumbnail_variant_urls?: ImageVariants[];
  allocations: PrizeAllocation[];
};

export type EventDetail = {
  id: number;
  uid: string;
  site_id: number;
  site?: {
    id: number;
    name: string;
    slug: string;
  };
  name: string;
  description: string;
  starts_at: string;
  ends_at: string;
  status: "draft" | "ready" | "published" | "active" | "finished" | "cancelled" | "archived";
  status_label?: string;
  products: Array<{ id: number; name: string }>;
  thumbnail_url?: string;
  thumbnail_variant_urls?: ImageVariants;
  overview_image_url?: string;
  overview_image_variant_urls?: ImageVariants;
  lottery_animation_url?: string;
  lottery_pools_count: number;
  lottery_pools_available_count: number;
  // 統合口数（新旧両方式対応）
  total_stock_count?: number;
  available_stock_count?: number;
  sold_stock_count?: number;
  uses_new_prize_system?: boolean;
  use_product_stock_lottery?: boolean;
  prizes_with_allocations?: PrizeWithAllocations[];
  gacha_cost: number;
  shipping_fee?: number;
  shipping_fee_label?: 'shipping_fee' | 'system_fee';
  delivery_estimate?: string;
  // くじ関連
  event_type?: 'oripa' | 'kuji';
  probability_mode?: 'stock_based' | 'fixed_rate';
  kuji_grades?: KujiGradeDetail[];
  // EC チェックアウトモード
  ec_checkout_mode?: boolean;
  // 配送手数料（段階制）
  free_shipping?: boolean;
  shipping_fee_tier_quantity?: number;
  shipping_fee_per_tier?: number;
  // 表示設定
  hidden_from_list?: boolean;
  // ログボガチャ（1日回数制限）
  daily_play_limit?: number;
  total_play_limit?: number;
  total_plays_remaining?: number;
  daily_plays_remaining?: number;
  // 新規登録ユーザー限定
  new_customer_only_days?: number;
  new_customer_eligible?: boolean | null;
  new_customer_days_remaining?: number | null;
  new_customer_expires_at?: string | null;
  // ポイントチャージ実績条件
  paid_amount_requirement_scope?: 'cumulative' | 'monthly' | null;
  paid_amount_requirement_minimum?: number | null;
  paid_amount_requirement_label?: string | null;
  paid_amount_requirement_current_amount?: number | null;
  paid_amount_requirement_eligible?: boolean | null;
  // 顧客リスト限定
  customer_list_restricted?: boolean;
  customer_list_eligible?: boolean | null;
  customer_list_condition_text?: string | null;
  // ラストワン賞
  last_one_prize?: {
    id: number;
    name: string;
    description?: string;
    image_url?: string;
    image_variants?: ImageVariants;
  };
  // セット購入特典
  bulk_bonuses?: {
    quantity: number;
    label?: string;
    product: {
      id: number;
      name: string;
      description?: string;
      image_url?: string;
      image_variants?: ImageVariants;
    };
  }[];
};

// くじ等級の型（一覧用）
export type KujiGrade = {
  name: string;
  title?: string | null;
  description?: string | null;
  initial_stock?: number;
  remaining_stock?: number;
  probability?: number;
};

// くじ等級の型（詳細用）
export type KujiGradeDetail = {
  id: number;
  name: string;
  title?: string | null;
  description?: string | null;
  position: number;
  initial_stock?: number;
  remaining_stock?: number;
  unlimited_stock?: boolean;
  sold_out?: boolean;
  probability?: number;
  products: Array<{
    id: number;
    name: string;
    description?: string | null;
    image_url: string | null;
    image_variants?: ImageVariants;
    remaining_stock?: number; // 在庫連動型の場合のみ
  }>;
};

export type Event = {
  id: number;
  uid: string; // UID を含む
  name: string;
  description?: string;
  starts_at: string;
  ends_at: string;
  status: "draft" | "ready" | "published" | "active" | "finished" | "cancelled" | "archived";
  status_label?: string;
  actually_started_at?: string;
  actually_ended_at?: string;
  thumbnail_url?: string;
  thumbnail_variant_urls?: ImageVariants;
  overview_image_url?: string;
  overview_image_variant_urls?: ImageVariants;
  lottery_animation_url?: string;
  lottery_pools_count?: number;
  lottery_pools_available_count?: number;
  lottery_pools_sold_count?: number;
  // 統合口数（新旧両方式対応）
  total_stock_count?: number;
  available_stock_count?: number;
  sold_stock_count?: number;
  uses_new_prize_system?: boolean;
  use_product_stock_lottery?: boolean;
  prize_images?: string[];
  prize_image_variant_urls?: Array<ImageVariants | null>;
  gacha_cost: number;
  // くじ関連
  event_type?: 'oripa' | 'kuji';
  probability_mode?: 'stock_based' | 'fixed_rate';
  kuji_grades?: KujiGrade[];
  hidden_from_list?: boolean;
  // ログボガチャ（1日回数制限）
  daily_play_limit?: number;
  total_play_limit?: number;
  total_plays_remaining?: number;
  daily_plays_remaining?: number;
  // 新規登録ユーザー限定
  new_customer_only_days?: number;
  new_customer_eligible?: boolean | null;
  new_customer_days_remaining?: number | null;
  new_customer_expires_at?: string | null;
  // ポイントチャージ実績条件
  paid_amount_requirement_scope?: 'cumulative' | 'monthly' | null;
  paid_amount_requirement_minimum?: number | null;
  paid_amount_requirement_label?: string | null;
  paid_amount_requirement_current_amount?: number | null;
  paid_amount_requirement_eligible?: boolean | null;
  // 顧客リスト限定
  customer_list_restricted?: boolean;
  customer_list_eligible?: boolean | null;
  customer_list_condition_text?: string | null;
};

export type Category = {
  id: number;
  name: string;
  slug: string;
};

export type Tag = {
  id: number;
  name: string;
  slug: string;
};

export const fetchSites = async (): Promise<Site[]> => {
  const response = await apiClient.get<Site[]>("/customer/sites");
  return response.data;
};

export const fetchSiteDetail = async (siteId: number): Promise<Site> => {
  const response = await apiClient.get<Site>(`/customer/sites/${siteId}`);
  return response.data;
};

export const fetchEventDetail = async (siteId: number, uid: string): Promise<EventDetail> => {
  const response = await apiClient.get<EventDetail>(`/customer/sites/${siteId}/events/${uid}`);
  return response.data;
};

// 個別イベントの在庫情報を取得（キャッシュなし）
export const fetchEventDetailStock = async (siteId: number, uid: string): Promise<EventStock> => {
  const response = await apiClient.get<EventStock>(`/customer/sites/${siteId}/events/${uid}/stock`);
  return response.data;
};

// 在庫情報の型（/events/stock エンドポイント用）
export type EventStock = {
  id: number;
  uid: string;
  status?: Event["status"];
  daily_play_limit?: number;
  total_play_limit?: number;
  total_plays_remaining?: number;
  daily_plays_remaining?: number;
  new_customer_only_days?: number;
  new_customer_eligible?: boolean | null;
  new_customer_days_remaining?: number | null;
  new_customer_expires_at?: string | null;
  uses_prize_system?: boolean;
  lottery_pools_count?: number;
  lottery_pools_available_count?: number;
  lottery_pools_sold_count?: number;
  total_stock_count?: number;
  available_stock_count?: number;
  sold_stock_count?: number;
  paid_amount_requirement_scope?: 'cumulative' | 'monthly' | null;
  paid_amount_requirement_minimum?: number | null;
  paid_amount_requirement_label?: string | null;
  paid_amount_requirement_current_amount?: number | null;
  paid_amount_requirement_eligible?: boolean | null;
  customer_list_restricted?: boolean;
  customer_list_eligible?: boolean | null;
  customer_list_condition_text?: string | null;
  kuji_grades?: Array<{
    name: string;
    initial_stock?: number;
    remaining_stock?: number;
    probability?: number;
  }>;
};

export const fetchSiteEventsPage = async (
  siteId: number,
  page: number,
  categorySlug?: string,
  tagSlugs?: string[]
): Promise<{ events: Event[]; next_page: number | null }> => {
  const params = new URLSearchParams({ page: String(page) });
  if (categorySlug && categorySlug !== 'all') params.set('category_slug', categorySlug);
  if (tagSlugs?.length) params.set('tag_slugs', tagSlugs.join(','));
  const response = await apiClient.get<{ events: Event[]; next_page: number | null }>(
    `/customer/sites/${siteId}/events?${params}`
  );
  return response.data;
};

export const fetchSiteEvents = async (
  siteId: number,
  categorySlug?: string,
  tagSlugs?: string[]
): Promise<Event[]> => {
  let eventsUrl = `/customer/sites/${siteId}/events`;
  const stockUrl = `/customer/sites/${siteId}/events/stock`;
  const params = new URLSearchParams();

  if (categorySlug) params.append("category_slug", categorySlug);
  if (tagSlugs && tagSlugs.length > 0) params.append("tag_slugs", tagSlugs.join(","));

  if (params.toString()) eventsUrl += `?${params.toString()}`;

  // 静的情報と在庫情報を並列で取得
  const [eventsResponse, stockResponse] = await Promise.all([
    apiClient.get<Event[]>(eventsUrl),
    apiClient.get<EventStock[]>(stockUrl),
  ]);

  const events = eventsResponse.data;
  const stocks = stockResponse.data;

  // 在庫情報をマージ
  return events.map((event) => {
    const stock = stocks.find((s) => s.id === event.id);
    return stock ? { ...event, ...stock } : event;
  });
};

export const fetchSiteCategories = async (siteId: number): Promise<Category[]> => {
  const response = await apiClient.get<Category[]>(`/customer/sites/${siteId}/categories`);
  return response.data;
};

export const fetchSiteTags = async (siteId: number): Promise<Tag[]> => {
  const response = await apiClient.get<Tag[]>(`/customer/sites/${siteId}/tags`);
  return response.data;
};

export type HomeAddress = {
  id: number;
  name: string;
  postal_code: string;
  address_line1: string;
  address_line2?: string;
  phone_number: string;
  default: boolean;
};

export const fetchHomeAddresses = async (): Promise<HomeAddress[]> => {
  const response = await apiClient.get<HomeAddress[]>("/customer/home_addresses");
  return response.data;
};

export const createHomeAddress = async (address: Omit<HomeAddress, "id">): Promise<HomeAddress> => {
  const response = await apiClient.post<HomeAddress>("/customer/home_addresses", address);
  return response.data;
};

export const updateHomeAddress = async (id: number, address: Partial<HomeAddress>): Promise<HomeAddress> => {
  const response = await apiClient.put<HomeAddress>(`/customer/home_addresses/${id}`, address);
  return response.data;
};

export const deleteHomeAddress = async (id: number): Promise<void> => {
  await apiClient.delete(`/customer/home_addresses/${id}`);
};

export type DrawResultItem = {
  id?: number;
  success?: boolean;
  prize_name?: string;
  grade_name?: string;
  message?: string;
  product_image_url?: string;
  animation_url?: string | null;
  product?: {
    id: number;
    name: string;
    image_url?: string;
  } | null;
};

export type DrawResult = {
  success: boolean;
  prize_name?: string;
  message?: string;
  product_image_url?: string;
  lottery_result_ids?: number[];
  results?: DrawResultItem[];
};

export const drawLottery = async (siteId: number, eventId: string, count: number = 1): Promise<DrawResult> => {
  const response = await apiClient.post(`/customer/sites/${siteId}/events/${eventId}/lottery`, { count });
  return response.data;
};

export const fetchMe = async (): Promise<any> => {
  const response = await apiClient.get("/me");
  return response.data;
}

export const updateMySettings = async (settings: {
  lottery_animation_enabled?: boolean;
}): Promise<{ current_user: any }> => {
  const response = await apiClient.patch("/me", settings);
  return response.data;
};

export const updateEmail = async (email: string): Promise<{ message: string; pending_email: string }> => {
  const response = await apiClient.post("/me/update_email", {
    email: email
  });
  return response.data;
};

export const confirmEmailChange = async (token: string): Promise<{ message: string; current_user: any }> => {
  const response = await apiClient.post(`/me/confirm_email_change/${token}`);
  return response.data;
};

export type Order = {
  id: number;
  status: string;
  tracking_number?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  shipped_at?: string;
  received_at?: string;
  lottery_results: Array<{
    id: number;
    drawn_at: string;
    lottery_pool?: {
      id: number;
      prize_allocation?: {
        id: number;
        product?: {
          id: number;
          name: string;
          description: string;
        };
        prize?: {
          id: number;
          name: string;
          description: string;
        };
      };
    };
  }>;
};

export const fetchOrders = async (): Promise<Order[]> => {
  const response = await apiClient.get('/customer/orders');
  return response.data;
};

export const confirmReceipt = async (orderId: number): Promise<Order> => {
  const response = await apiClient.patch(`/customer/orders/${orderId}/confirm_receipt`);
  return response.data;
};

export type AccountTransaction = {
  id: number;
  amount: number;
  currency: string;
  operation_type: string;
  created_at: string;
  expires_at: string | null;
  remaining_amount: number | null;
  expired: boolean;
  operation: {
    type: string;
    description: string;
    event_name?: string;
  } | null;
};

export type AccountTransactionsResponse = {
  transactions: AccountTransaction[];
  balance: number;
  expiring_soon: number;
  next_expiration: {
    expires_at: string;
    amount: number;
  } | null;
};

export const fetchAccountTransactions = async (): Promise<AccountTransactionsResponse> => {
  const response = await apiClient.get<AccountTransactionsResponse>('/customer/account_transactions');
  return response.data;
};

export type PointCharge = {
  id: number;
  amount: number;
  charge_type: string;
  status: 'completed' | 'failed' | 'unknown';
  status_label: string;
  site_name: string | null;
  payment_uid: string | null;
  created_at: string;
};

export const fetchPointCharges = async (): Promise<PointCharge[]> => {
  const response = await apiClient.get('/customer/point_charges');
  return response.data;
};

export type PendingPointPayment = {
  payment_uid: string;
  amount: number;
  status: 'pending' | 'awaiting_payment';
  status_label: string;
  expires_at: string | null;
  created_at: string;
};

export const fetchPendingPointPayments = async (): Promise<PendingPointPayment[]> => {
  const response = await apiClient.get<PendingPointPayment[]>('/customer/point_charges/pending_payments');
  return response.data;
};

export type Notification = {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  read_at: string | null;
  action_url: string | null;
  created_at: string;
  notifiable: {
    type: string;
    id: number;
  } | null;
};

export const fetchNotifications = async (): Promise<{ notifications: Notification[]; unread_count: number }> => {
  const response = await apiClient.get('/customer/notifications');
  return response.data;
};

export const fetchUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get('/customer/notifications/unread_count');
  return response.data.unread_count;
};

export const markNotificationAsRead = async (id: number): Promise<void> => {
  await apiClient.put(`/customer/notifications/${id}/mark_as_read`);
};

export const markAllNotificationsAsRead = async (): Promise<void> => {
  await apiClient.put('/customer/notifications/mark_all_as_read');
};

export const deleteNotification = async (id: number): Promise<void> => {
  await apiClient.delete(`/customer/notifications/${id}`);
};

export type Product = {
  lottery_result_id: number;
  id: number;
  name: string;
  image_urls: string[];
  image_variant_urls?: ImageVariants;
  rarity: string;
  drawn_at: string;
  point_value: number;
  status: 'pending' | 'shipping' | 'shipped' | 'converted' | 'invalidated';
  order_id?: number;
  order_status?: string;
  order_tracking_number?: string;
  // ポイント交換関連
  allow_point_conversion?: boolean;
  point_converted_at?: string;
  converted_point_value?: number;
  // コンテンツタイプ
  content_type: 'physical' | 'digital';
  // デジタルコンテンツのダウンロード状態
  downloaded?: boolean;
  downloaded_at?: string;
  // イベントタイプ
  event_type: 'oripa' | 'kuji';
  // 自動配送期限（くじ用）
  auto_ship_deadline?: string;
  can_request_shipping?: boolean;
  shipping_request_deadline_at?: string | null;
  point_exchange_deadline_at?: string | null;
  // イベント情報
  event_name?: string;
  event_uid?: string;
  // 配送料（オリパのみ。null は未設定=送料無料）
  shipping_fee?: number | null;
  // グルーピング
  checkout_group_id?: string;
  bulk_purchase_quantity?: number;
  is_bulk_bonus?: boolean;
  site: {
    id: number;
    name: string;
    subdomain: string;
  };
};

export const fetchProducts = async (): Promise<Product[]> => {
  const response = await apiClient.get('/customer/products');
  return response.data;
};

export type ProductStatusFilter = 'pending' | 'shipping' | 'shipped' | 'converted' | 'downloaded' | 'invalidated';

export type ProductPage = {
  products: Product[];
  pagination: {
    page: number;
    per_page: number;
    has_more: boolean;
  };
};

export const fetchProductPage = async (params: {
  eventType: 'oripa' | 'kuji';
  status: ProductStatusFilter;
  page?: number;
  perPage?: number;
}): Promise<ProductPage> => {
  const response = await apiClient.get('/customer/products', {
    params: {
      paginated: true,
      event_type: params.eventType,
      status: params.status,
      page: params.page || 1,
      per_page: params.perPage || 100,
    },
  });
  return response.data;
};

export const shipProducts = async (lotteryResultIds: number[]): Promise<{ message: string; order_ids: number[]; total_orders: number; total_items: number; errors: string[] }> => {
  const response = await apiClient.post('/customer/products/ship', { lottery_result_ids: lotteryResultIds });
  return response.data;
};

export type ShippingPreviewSite = {
  site_id: number;
  site_name: string;
  event_name?: string;
  shipping_fee: number;
  prepaid: boolean;
  item_count: number;
};

export type ShippingPreview = {
  site_breakdown: ShippingPreviewSite[];
  total_shipping_fee: number;
  customer_balance: number;
  sufficient_balance: boolean;
  invalid_lottery_result_ids: number[];
  address_registered: boolean;
  has_digital_items: boolean;
};

export const previewShipping = async (lotteryResultIds: number[]): Promise<ShippingPreview> => {
  const response = await apiClient.post('/customer/products/shipping_preview', { lottery_result_ids: lotteryResultIds });
  return response.data;
};

export const convertToPoints = async (lotteryResultIds: number[]): Promise<{ message: string; total_points: number; converted_count: number; errors: string[] }> => {
  const response = await apiClient.post('/customer/products/convert_to_points', { lottery_result_ids: lotteryResultIds });
  return response.data;
};

export const downloadDigitalProduct = async (lotteryResultId: number): Promise<{ download_url: string }> => {
  const response = await apiClient.get(`/customer/products/${lotteryResultId}/download`);
  return response.data;
};

export type ContactCategory = 'onboarding' | 'general' | 'technical' | 'billing' | 'account' | 'feature_request' | 'bug_report' | 'other';

export type Contact = {
  id: number;
  contact_number: string;
  name: string;
  email: string;
  phone?: string;
  category: ContactCategory;
  subject: string;
  message: string;
  created_at: string;
};

export type CreateContactPayload = {
  name: string;
  email: string;
  phone?: string;
  category: ContactCategory;
  subject: string;
  message: string;
};

export const createContact = async (contact: CreateContactPayload): Promise<{ id: number; contact_number: string; created_at: string; message: string }> => {
  const response = await apiClient.post('/customer/contacts', { contact });
  return response.data;
};

export const createJoybaseContact = async (
  contact: CreateContactPayload,
  recaptchaToken?: string
): Promise<{ id: number; contact_number: string; created_at: string; message: string }> => {
  const response = await apiClient.post('/contacts', {
    contact,
    recaptcha_token: recaptchaToken
  });
  return response.data;
};

// 電話番号認証
export const sendPhoneVerification = async (phoneNumber: string, method: 'sms' | 'call' = 'sms'): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post('/phone_verifications', {
    phone_number: phoneNumber,
    method: method
  });
  return response.data;
};

export const verifyPhoneCode = async (phoneNumber: string, code: string): Promise<{ success: boolean; message: string; current_user: any }> => {
  const response = await apiClient.post('/phone_verifications/verify', {
    phone_number: phoneNumber,
    code: code
  });
  return response.data;
};

// 通知設定
export type NotificationSettings = {
  login_notification: boolean;
  shipping_notification: boolean;
  lottery_notification: boolean;
  marketing_notification: boolean;
};

export const fetchNotificationSettings = async (): Promise<NotificationSettings> => {
  const response = await apiClient.get<NotificationSettings>('/customer/notification_settings');
  return response.data;
};

export const updateNotificationSettings = async (
  settings: Partial<NotificationSettings>
): Promise<NotificationSettings & { message: string }> => {
  const response = await apiClient.patch('/customer/notification_settings', settings);
  return response.data;
};

// 友達招待
export type ReferralInfo = {
  enabled: boolean;
  referral_code?: string;
  referral_url?: string;
  inviter_points?: number;
  invitee_points?: number;
  campaign_active?: boolean;
  conversion_point?: 'registration' | 'phone_verified' | 'first_purchase';
  total_invited?: number;
  total_points_earned?: number;
  max_invitations_per_inviter?: number | null;
  remaining_invitations?: number | null;
};

export type ReferralRecord = {
  id: number;
  status: 'pending' | 'completed' | 'expired';
  points_awarded: number;
  completed_at: string | null;
  created_at: string;
};

export const fetchReferralInfo = async (): Promise<ReferralInfo> => {
  const response = await apiClient.get<ReferralInfo>('/customer/referral');
  return response.data;
};

export const fetchReferralHistory = async (): Promise<{ referrals: ReferralRecord[] }> => {
  const response = await apiClient.get('/customer/referral/referrals');
  return response.data;
};

// クーポン
export type CouponStatus = {
  enabled: boolean;
};

export const fetchCouponStatus = async (): Promise<CouponStatus> => {
  const response = await apiClient.get<CouponStatus>('/customer/coupons/status');
  return response.data;
};

export type CouponRedeemResponse = {
  success: boolean;
  points_awarded: number;
  message: string;
};

export const redeemCoupon = async (code: string): Promise<CouponRedeemResponse> => {
  const response = await apiClient.post('/customer/coupons/redeem', { code });
  return response.data;
};

// くじ EC チェックアウト
export type KujiCheckoutPreview = {
  event: {
    id: number;
    uid: string;
    name: string;
    gacha_cost: number;
    shipping_fee: number;
    shipping_fee_label?: 'shipping_fee' | 'system_fee';
    thumbnail_url?: string;
    delivery_estimate?: string;
    lottery_animation_url?: string;
  };
  kuji_config?: {
    free_shipping: boolean;
    shipping_fee_tier_quantity: number;
    shipping_fee_per_tier: number;
    min_quantity: number;
    max_quantity: number;
    bulk_quantities: number[];
    bulk_bonuses?: Array<{
      quantity: number;
      label?: string;
      product: {
        id: number;
        name: string;
        image_url?: string;
        image_variants?: ImageVariants;
      } | null;
    }>;
    remaining_stock: number;
  };
  cost: {
    unit_price: number;
    quantity: number;
    kuji_cost: number;
    shipping_fee: number;
    total: number;
  };
  addresses: Array<{
    id: number;
    name: string;
    postal_code: string;
    address_line1: string;
    address_line2?: string;
    phone_number: string;
    default: boolean;
  }>;
  current_balance: number;
  max_points_usable: number;
  card_payment_enabled?: boolean;
  // 軸2: Owner が顧客に出す決済手段（顧客側の決済手段表示の単一ソース）
  accepted_payment_methods?: string[];
  payment_methods?: Array<{
    id: number;
    last4: string;
    brand: string;
    exp_month: number;
    exp_year: number;
    is_default: boolean;
    expired: boolean;
  }>;
  payment_provider?: 'fincode' | 'gmo_pg';
  gmo_pg_shop_id?: string;
  test_mode?: boolean;
};

export type KujiCardCheckoutResponse = {
  requires_action?: boolean;
  redirect_url?: string;
  payment_uid?: string;
  success?: boolean;
  // 全額ポイント払い時の追加フィールド
  full_point_payment?: boolean;
  checkout_uid?: string;
  results?: KujiCheckoutResult['results'];
  summary?: KujiCheckoutResult['summary'];
};

export type KujiCardCheckoutStatus = {
  payment_uid: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  amount: number;
  event_uid?: string; // イベントUID（リトライ用）
  event_name?: string; // イベント名
  lottery_animation_url?: string;
  shipping_fee_label?: 'shipping_fee' | 'system_fee';
  results?: KujiCheckoutResult['results'];
  summary?: KujiCheckoutResult['summary'];
  error_message?: string; // 失敗時のエラーメッセージ
};

export type KujiCheckoutResult = {
  success: boolean;
  results: Array<{
    grade_id: number | null;
    grade_name: string;
    grade_animation_url?: string | null;
    product_id?: number;
    product_name?: string;
    image_url?: string;
    is_last_one_prize?: boolean;
  }>;
  summary: {
    total_quantity: number;
    kuji_cost: number;
    shipping_fee: number;
    points_used?: number;
    card_amount?: number;
    total_paid: number;
  };
  new_balance: number;
};

export const fetchKujiCheckoutPreview = async (
  siteId: number,
  eventId: string,
  quantity: number = 1
): Promise<KujiCheckoutPreview> => {
  const response = await apiClient.get<KujiCheckoutPreview>(
    `/customer/sites/${siteId}/kuji/${eventId}/checkout_preview`,
    { params: { quantity } }
  );
  return response.data;
};

export const submitKujiCheckoutWithCard = async (
  siteId: number,
  eventId: string,
  quantity: number,
  homeAddressId: number,
  paymentMethodId: number | null,
  isBulkPurchase: boolean = false,
  pointsToUse: number = 0
): Promise<KujiCardCheckoutResponse> => {
  try {
    const response = await apiClient.post<KujiCardCheckoutResponse>(
      `/customer/sites/${siteId}/kuji/${eventId}/checkout_with_card`,
      {
        quantity,
        home_address_id: homeAddressId,
        payment_method_id: paymentMethodId,
        is_bulk_purchase: isBulkPurchase,
        points_to_use: pointsToUse,
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.error || "カード決済に失敗しました");
    }
    throw new Error("カード決済に失敗しました");
  }
};

export const checkKujiCardCheckoutStatus = async (
  siteId: number,
  paymentUid: string
): Promise<KujiCardCheckoutStatus> => {
  const response = await apiClient.get<KujiCardCheckoutStatus>(
    `/customer/sites/${siteId}/kuji/card_checkout_status/${paymentUid}`
  );
  return response.data;
};
